<template>
  <div class="header">
    <div class="header__inner">
      <!-- Left / Logo -->
      <div class="header__logo flex items-center">
        <div class="text-foregroundPrimary cursor-pointer flex items-center" @click="logoClick">
          <SezusMainLogo :isDark="isDarkMode" class="w-[130px] h-[40px]" />
        </div>
      </div>

      <!-- Middle align zone (center column reference). Place search bar inside if needed. -->
      <div class="header__middle hidden sm:block">
        <!-- <div class="aligned-center">
          <div class="relative w-full max-w-md">
            <span class="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24"
                stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M21 21l-4.35-4.35M11 18a7 7 0 100-14 7 7 0 000 14z" />
              </svg>
            </span>
            <input type="text" v-model="searchQuery" placeholder="Search Sez Us..."
              class="w-full rounded-full border border-[#F4E6FF]-300 bg-white py-2 pl-10 pr-4 text-sm text-gray-700 placeholder-[#A46CCF] placeholder:font-semibold focus:ring-1 focus:ring-[#A46CCF] focus:outline-none focus:ring-1 focus:ring-blue-500" />
          </div>
        </div> -->
        <div class="navbar-search relative w-full sm:block hidden">
          <!-- 🔍 Search Input -->
          <BaseField v-model:value="searchText" @input="fetchSearchResults" class="navbar-search__input mt-0"
            placeholder="Search..." isSearch isBottomBorderOnFocus />

          <!-- 🧭 Dropdown Results -->
          <div v-if="isLoading || isSearchActive" ref="dropdownRef"
            class="absolute top-full left-0 mt-2 w-full overflow-y-auto rounded-xl shadow-lg bg-[var(--theme-wise-background)] z-50 p-3">
            <div v-if="isLoading" class="flex justify-center items-center py-6">
              <div class="profile__posts text-center mt-[6vh]" v-if="isLoading">
                <ProgressSpinner style="width: 40px; height: 40px; margin: 0 auto" strokeWidth="5" fill="transparent"
                  animationDuration=".8s" aria-label="Custom ProgressSpinner" />
              </div>
            </div>
            <!-- People Results -->
            <div v-if="allUsers.length">
              <h4 class="font-semibold text-[var(--text-primary)] mb-2">People ({{ allUsers.length }})</h4>
              <div v-for="user in displayedUsers" :key="user.id" class="mb-2">
                <SuggestedPost :data="user" type="user" :onCloseDropdown="closeDropdown" />
              </div>
            </div>

            <!-- Post Results -->
            <div v-if="allPosts.length" class="mt-4">
              <h4 class="font-semibold text-[var(--text-primary)] mb-2">Posts ({{ allPosts.length }})</h4>
              <div v-for="post in displayedPosts" :key="post.id" class="mb-2" @click="closeDropdown">
                <CompactPost :post="post" :onCloseDropdown="closeDropdown" />
              </div>
            </div>

            <!-- Group Results -->
            <div v-if="allGroups.length" class="mt-4">
              <h4 class="font-semibold text-[var(--text-primary)] mb-2">Groups ({{ allGroups.length }})</h4>
              <div v-for="group in limitedGroups" :key="group.id" class="mb-2" @click="closeDropdown">
                <GroupItem :group="group" :showFollowButton="true" @follow="followGroup(group)" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- For authenticated users -->
      <div class="header__right hidden sm:flex items-center" v-if="isUserAuth && userdata">
        <Dropdown :pt="{ panel: { class: 'header-dropdown-panel' } }"
          class="current-org-drops w-[10rem] flex items-center justify-center rounded-[10px] w-[11rem] font-bold !px-0"
          v-model="selectedProfile" :options="filteredOrgs" optionLabel="name"
          @change="setCurrentProfile(selectedProfile)" placeholder="Select profile">
          <template #value="slotProps">
            <div v-if="slotProps.value" class="flex align-items-center">
              <BaseAvatar :storage-id="slotProps.value?.avatarFile?.storageId" classes="header-dropdown-avatar"
                :rating="userdata?.rating" />
              <div class="!text-[var(--tw-text-opacity, 1)] !text-[0.7rem] flex items-center">
                <span class="dropdown-menu__caption !text-[0.7rem]"> &nbsp; {{ slotProps.value.username.length > 20 ?
                  slotProps.value.username.slice(0, 20) + ".." : slotProps.value.username }} </span>
              </div>
            </div>
            <span v-else>{{ slotProps.placeholder }}</span>
          </template>
          <template #option="slotProps">
            <div class="flex justify-center items-center">
              <BaseAvatar :storage-id="slotProps.option?.avatarFile?.storageId" class="header-dropdown-avatar"
                :rating="slotProps.option?.rating" />

              <div class="!text-[var(--tw-text-opacity, 1)] text-[0.9rem] ml-1">
                <span class="dropdown-menu__caption">{{ slotProps.option.username }}</span>
              </div>
            </div>
          </template>
        </Dropdown>
        <router-link to="/" class="flex items-center">
          <component :is="isHomePage ? HomeBadge : HomeBadgeDeactive" />
        </router-link>
        <router-link to="/notifications" class="flex items-center relative">
          <component :is="isNotificationsPage ? NotificationIconActive : NotificationIconDeactive"
            :class="{ 'bell-shake': isShaking }" />
          <span v-if="unreadNotificationsCount > 0"
            class="absolute -top-[9px] -right-[9px] bg-red-500 text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
            {{ unreadNotificationsCount > 99 ? "99+" : unreadNotificationsCount }}
          </span>
        </router-link>
        <router-link class="header__settings flex cursor-pointer items-center" :to="createUserRoute()">
          <BaseAvatar :storage-id="userdata?.avatarFile?.storageId" :href="createUserRoute()" :isAdmin="isAdmin"
            :verified="userdata?.verified" :rating="userdata?.rating" :isCurrentUser="true" />
        </router-link>
        <!-- <div class="relative header__settings flex items-center" ref="avatarDropdownRef"> -->
        <!-- Avatar button -->
        <!-- <div @click.stop="toggleAvatarDropdown" class="cursor-pointer flex items-center relative z-[10000]">
            <img
              :src="userdata?.avatarFile?.storageId ? BaseURL + userdata.avatarFile.storageId : '/images/DefaultAvatar.svg'"
              alt="User Avatar" class="w-10 h-10 rounded-full border border-gray-400 object-cover" />
          </div> -->

        <!-- Dropdown menu -->
        <!-- <transition name="fade">
            <div v-if="showAvatarDropdown"
              class="absolute right-0 w-48 rounded-lg shadow-lg bg-white border border-gray-200 z-[9999]"
              style="top: auto; bottom: calc(100% + -7.9rem); left: -8rem;">
              <ul class="py-2 text-sm text-gray-700">
                <li class="px-4 py-2 hover:bg-gray-100 cursor-pointer" @click="handleAvatarOption('Profile')">
                  Profile
                </li>
                <li class="px-4 py-2 hover:bg-gray-100 cursor-pointer text-red-500"
                  @click="handleAvatarOption('Logout')">
                  Logout
                </li>
              </ul>
            </div>
          </transition> -->
        <!-- <transition name="fade"> -->
        <!-- <div v-if="showAvatarDropdown"
              class="absolute right-0 w-64 bg-white rounded-2xl shadow-xl border border-gray-200 z-[9999] py-2"
              style="top: auto; bottom: calc(100% + -20rem); left: -10rem;"> -->
        <!-- <div v-if="showAvatarDropdown"
              class="absolute right-0 w-64 bg-[var(--theme-wise-background)] rounded-2xl shadow-xl border border-gray-200 z-[9999] py-2"
              style="top: 110%;"> -->
        <!-- Profile Section -->
        <!-- <div
                class="flex items-center gap-3 px-4 py-2 hover:bg-[var(--theme-wise-hover)] cursor-pointer rounded-lg transition"
                @click="handleAvatarOption('Profile')">
                <div class="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                  <img :src="userdata?.avatarFile?.storageId ? BaseURL + userdata.avatarFile.storageId : defaultImage"
                    alt="User Avatar" class="w-10 h-10 rounded-full border border-gray-400 object-cover" />
                </div>
                <div> -->
        <!-- <p class="font-semibold text-foregroundPrimary">
                    {{ userdata?.firstName }} {{ userdata?.lastName }}
                  </p> -->
        <!-- <p class="text-sm text-gray-500">See all profiles</p> -->
        <!-- </div>
              </div> -->

        <!-- Groups List -->
        <!-- <div class="px-4 mt-3">
                <p class="text-xs text-gray-500 font-semibold mb-2">Your Groups</p>

                <div v-for="(group, index) in myOrgs" :key="index"
                  class="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-all"
                  :class="selectedGroup?.id === group.id ? 'bg-[#e6d6e0] border border-[#e6d6e0]' : 'hover:bg-[var(--theme-wise-hover)]'"
                  @click="selectGroup(group)">
                  <BaseAvatar :storage-id="group?.avatarFile?.storageId" class="!max-w-[28px] !max-h-[28px]" />
                  <span class="text-sm font-medium truncate"
                    :class="selectedGroup?.id === group.id ? 'text-[#843688]-600' : 'text-foregroundPrimary'">
                    {{ group.name }}
                  </span>
                </div>
              </div> -->

        <!-- <hr class="my-3 border-gray-200" /> -->
        <!-- Menu Options -->
        <!-- <ul class="text-gray-700 text-sm">
                <li class="flex items-center gap-3 px-4 py-2 hover:bg-gray-100 cursor-pointer transition"
                  @click="handleAvatarOption('Settings')">
                  <i class="pi pi-cog text-gray-500"></i>
                  Settings & Privacy
                </li>
                <li class="flex items-center gap-3 px-4 py-2 hover:bg-gray-100 cursor-pointer transition"
                  @click="handleAvatarOption('Help')">
                  <i class="pi pi-question-circle text-gray-500"></i>
                  Help & Support
                </li>
                <li class="flex items-center gap-3 px-4 py-2 hover:bg-gray-100 cursor-pointer transition"
                  @click="handleAvatarOption('Display')">
                  <i class="pi pi-moon text-gray-500"></i>
                  Display & Accessibility
                </li>
                <li class="flex items-center gap-3 px-4 py-2 hover:bg-gray-100 cursor-pointer transition"
                  @click="handleAvatarOption('Feedback')">
                  <i class="pi pi-comment text-gray-500"></i>
                  Give Feedback
                </li>
              </ul> -->

        <!-- <hr class="my-2 border-gray-200" /> -->

        <!-- Logout -->
        <!-- <div
                class="flex items-center gap-3 px-4 py-2 hover:bg-[var(--theme-wise-hover)] cursor-pointer text-red-500 font-medium rounded-lg transition"
                @click="handleAvatarOption('Logout')">
                <i class="pi pi-sign-out"></i>
                Log out
              </div>
            </div>
          </transition> -->



        <!-- </div> -->


        <!-- <ColorSchemeSwitcher class="theme-btn" v-model:showAvatarDropdown="showAvatarDropdown" /> -->
        <!-- change them and notification -->
        <!-- <div class="badge-wrap">
          <ReputationBadge v-if="userdata?.rating" :score="userdata.rating" :showTierName="true" :isCurrentUser="true"
            :tierSet="tierSet" class="prominent-badge" />
        </div>
        <div @click="toNotifications" class="header__notif flex items-center cursor-pointer text-iconColor02 relative">
          <img :src="getAlertIcon" alt="Alerts" class="w-[20px] h-[20px]" :class="{ 'bell-shake': isShaking }" />
          <span v-if="unreadNotificationsCount > 0"
            class="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
            {{ unreadNotificationsCount > 99 ? "99+" : unreadNotificationsCount }}
          </span>
        </div>
        <ColorSchemeSwitcher class="header__theme" /> -->
      </div>

      <!-- For non-authenticated users -->
      <!-- <div class="guest-buttons" v-else>
        <router-link to="/about" class="about-link">
          <ChannelIcon class="w-[18px] mr-1" />
          <span class="about-text">About Us</span>
        </router-link>
        <BaseButton class="sign-in-btn" @click="openAuthChoices('sign-in')">
          <img src="/icon/person.svg" alt="Sign in" class="button-icon" />
          <span class="button-text">Sign in</span>
        </BaseButton>
        <BaseButton class="sign-up-btn" @click="goCreateUsername">
          <img src="/icon/Create.svg" alt="Sign up" class="button-icon" />
          <span class="button-text">Sign up</span>
        </BaseButton>
        <SIWFButton class="frequency-btn" @click="siwf" />
      </div> -->

      <!-- Theme selector - right justified -->
      <!-- <div class="theme-container" v-if="!isUserAuth"> -->

      <!-- </div> -->
      <div class="hamburger block sm:hidden flex items-center ml-auto">
        <button @click="toggleMenu" class="hamburger-btn" :class="{ open: isMenuOpen }" aria-label="Toggle menu">
          <component :is="isMenuOpen ? CloseIcon : HamburgerIcon" />
        </button>
      </div>
    </div>
    <transition name="slide-down">
      <div v-if="isMenuOpen"
        class="mobile-menu sm:hidden flex flex-col items-center bg-[var(--theme-wise-background)] w-full shadow-lg py-4 z-[999]">
        <!-- Color Scheme Switcher -->
        <!-- <ColorSchemeSwitcher class="mb-3 block sm:hidden" /> -->

        <!-- Profile Dropdown -->
        <Dropdown :pt="{ panel: { class: 'mobile-dropdown-panel' } }" class="mobile-org-dropdown w-[80%]"
          v-model="selectedProfile" :options="filteredOrgs" optionLabel="name"
          @change="setCurrentProfile(selectedProfile)" placeholder="Select profile">
          <template #value="slotProps">
            <div v-if="slotProps.value" class="flex items-center justify-center gap-2">
              <BaseAvatar :storage-id="slotProps.value?.avatarFile?.storageId" classes="header-dropdown-avatar" />
              <div class="!text-[var(--tw-text-opacity, 1)] !text-[0.7rem] flex items-center">
                <span class="dropdown-menu__caption !text-sm">{{ slotProps.value.username.length > 20 ?
                  slotProps.value.username.slice(0, 20) + ".." : slotProps.value.username }}</span>
              </div>
            </div>
            <span v-else>{{ slotProps.placeholder }}</span>
          </template>
          <template #option="slotProps">
            <div class="flex items-center justify-center gap-2">
              <BaseAvatar :storage-id="slotProps.option?.avatarFile?.storageId" class="w-8 h-8" />
              <span class="truncate text-sm font-medium">{{ slotProps.option.username }}</span>
            </div>
          </template>
        </Dropdown>

        <!-- <Dropdown
          :pt="{ panel: { class: 'mobile-dropdown-panel' } }"
          class="mobile-org-dropdown w-[80%]"
          v-model="selectedProfile"
          :options="filteredOrgs"
          optionLabel="name"
          @change="setCurrentProfile(selectedProfile)"
          placeholder="Select profile"
        >
          <template #value="slotProps">
            <div v-if="slotProps.value" class="flex items-center justify-center gap-2">
              <BaseAvatar :storage-id="slotProps.value?.avatarFile?.storageId" class="w-8 h-8" />
              <span class="truncate text-sm font-medium">{{ slotProps.value.username }}</span>
            </div>
            <span v-else>{{ slotProps.placeholder }}</span>
          </template>
        </Dropdown> -->
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import ProgressSpinner from "primevue/progressspinner";
import { RouterLink, useRouter, useRoute } from "vue-router";
import { useStore } from "vuex";
import Dropdown from "primevue/dropdown";
import ColorSchemeSwitcher from "@/components/ColorSchemeSwitcher/index.vue";
import SezusMainLogo from "@/components/Icons/SezusMainLogo.vue";
import ReputationBadge from "@/components/ReputationBadge.vue";
import SIWFButton from "@/components/ui-kit/SIWFButton/index.vue";
import ChannelIcon from "@/components/Icons/ChannelIcon.vue";
import HamburgerIcon from "@/components/Icons/HamburgerIcon.vue";
import CloseIcon from "@/components/Icons/CloseIcon.vue";
import eventBus from "@/utils/eventBus";
import { scoreToPercentileSync, formatPercentile } from "@/utils/utils.js";
// removed search field handling (moved to Rightbar)
import { useSchemeObserver } from "@/utils/colorSchemeUtils";
import { useTierSet } from "@/utils/reputationTiers.js";
import { makeLegacyDecoration } from "@/utils/urlDecoration.js";
import HomeIcon from "../Icons/HomeIcon.vue";
import HomeBadge from "../Icons/homeBadge.vue";
import HomeBadgeDeactive from "../Icons/HomeBadgeDeactive.vue";
import NotificationIconActive from "../Icons/NotificationIconActive.vue";
import NotificationIconDeactive from "../Icons/NotificationIconDeactive.vue";
import { useThemeScheme } from "@/composables/useThemeSchema";
import { BaseURL } from "@/global/baseUrls";
import defaultImage from "@/assets/images/DefaultAvatar.svg"
const store = useStore();
const route = useRoute();
const router = useRouter();
const userdata = computed(() => store.getters.getUser);
const myOrgs = computed(() => store.getters.getOnlyUserOrgs);
console.error(userdata.value.lastName, 'userdataaa'

);
const isMobile = ref(false);
const isHomePage = computed(() => route.path === "/");
const isNotificationsPage = computed(() => route.path === "/notifications");
const checkIfMobile = () => (isMobile.value = window.innerWidth <= 1024);
const dropdownRef = ref<HTMLElement | null>(null);
const userAuth = computed(() => store.getters.getUser);
const profileData = computed(() => store.getters.getUsernameData);
const logOut = (payload) => store.dispatch("logOut", payload);
// dark them image logic
const { isDarkMode } = useThemeScheme();
// Loader while fetching
const isLoading = ref(false);
const isMenuOpen = ref(false);

const toggleMenu = () => {
  isMenuOpen.value = !isMenuOpen.value;
};

onMounted(() => {
  checkIfMobile();
  window.addEventListener('resize', checkIfMobile);
});

// search bar moved to Rightbar; shared state remains in store

// Tier set for reputation badges
const { tierSet } = useTierSet();
const isUserAuth = computed(() => store.getters.getAuthStatus);
const isShowModal = (value) => store.dispatch("showModal", value);
const getMyOrgs = (search = "") =>
  store.dispatch("fetchOnlyUserOrgs", { search });
const handleSetOrgProfile = (data) =>
  store.dispatch("fetchCurrentProfileOrg", data);
const isAdmin = computed(
  () => userdata?.value?.roles?.includes("admin") || false
);
// const myOrgs = computed(() => store.getters.getOnlyUserOrgs);
const orgInfo = computed(() => store.getters.getOrgData);
const currentOrgProfile = computed(() => store.getters.getCurrentOrgProfile);
const filteredOrgs = ref([]);
const selectedProfile = ref(null);
const showAvatarDropdown = ref(false);
const showAvatarDropdownGroup = ref(false);
const selectedGroup = ref(null);
const avatarDropdownRef = ref<HTMLElement | null>(null);
const unreadNotificationsCount = computed(
  () => store.getters.getUnreadNotificationsCount
);
const isShaking = ref(false);
function selectGroup(group) {
  selectedGroup.value = group;
  showAvatarDropdown.value = !showAvatarDropdown.value;
  console.log('Selected Group:', group);
}
const hasUnreadNotification = computed(() => {
  const newNotificationsCount = store.getters.getUnreadNotificationsCount;
  return newNotificationsCount > 0;
});
// Toggle profile dropdown
const toggleAvatarDropdown = () => {
  showAvatarDropdown.value = !showAvatarDropdown.value;
  console.error("toggleAvatarDropdown called, new value:", showAvatarDropdown.value);
};

const handleAvatarOption = (option) => {
  showAvatarDropdown.value = false;

  if (option === "Profile") {
    router.push(createUserRoute());
  } else if (option === "Settings") {
    router.push("/settings");
  } else if (option === "Logout") {
    logOut({});
  }
};
const handleOutsideClick = (event: MouseEvent) => {
  if (
    avatarDropdownRef.value &&
    !avatarDropdownRef.value.contains(event.target as Node)
  ) {
    showAvatarDropdown.value = false;
  }
};

onMounted(() => {
  document.addEventListener("click", handleOutsideClick);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleOutsideClick);
});
// Trigger shake animation
const triggerShake = () => {
  isShaking.value = true;
  setTimeout(() => {
    isShaking.value = false;
  }, 500);
};

// Calculate the user's percentile based on their rating
const userPercentile = computed(() => {
  if (!userdata.value?.rating) return "-";
  const percentile = scoreToPercentileSync(userdata.value.rating);
  return formatPercentile(percentile, "short");
});

// Tooltip showing both percentile and raw score
const reputationTooltip = computed(() => {
  if (!userdata.value?.rating) return "Reputation: N/A";
  const percentile = scoreToPercentileSync(userdata.value.rating);
  const rank = formatPercentile(percentile, "rank");
  return `${rank} (Raw score: ${userdata.value.rating})`;
});

const createUserRoute = () => {
  const base = !userdata.value?.username
    ? `/profile/${userdata.value?.id}`
    : `/user/${userdata.value?.username}`;
  return `${base}${makeLegacyDecoration()}`;
};

watch(
  () => currentOrgProfile.value,
  () => {
    selectedProfile.value = currentOrgProfile.value;
  }
);

watch(
  () => route.name,
  (newId, oldId) => {
    // filteredOrgs.value = myOrgs.value.filter(org => {
    //   if (org.id === orgInfo.value?.id) {
    //     return org;
    //   }
    // });
  }
);

// Watch for notification count changes and trigger shake
watch(unreadNotificationsCount, (newCount, oldCount) => {
  // Only shake if count increased (new notification arrived)
  if (newCount > oldCount && oldCount !== undefined) {
    triggerShake();
  }
});

// Watch for user authentication to shake on login if notifications exist
watch(
  () => userdata.value,
  (newUser, oldUser) => {
    // Trigger shake when user logs in and has unread notifications
    if (newUser && !oldUser && unreadNotificationsCount.value > 0) {
      // Delay slightly to ensure UI is ready
      setTimeout(() => {
        triggerShake();
      }, 1000);
    }
  }
);

const handleClickOutside = (event) => {
  const target = event.target;
  if (dropdownRef.value && !dropdownRef.value.contains(target)) {
    closeDropdown();
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
})

onMounted(async () => {
  if (userdata.value?.id) {
    await getMyOrgs();
    selectedProfile.value = currentOrgProfile.value;
    filteredOrgs.value = myOrgs.value.filter((org) => {
      let user = org.members.filter(
        (us) => us.userId === userdata.value?.id
      )[0];
      if (user?.role && user?.role !== "reader" && org.isPrivate === false) {
        return org;
      }
    });
    filteredOrgs.value.unshift(userdata.value);
  }
});

const setCurrentProfile = (data) => {
  handleSetOrgProfile(data);
};

const siwf = () => {
  // Use SIWF login URL if available, otherwise fallback to sign-in-with-x route
  const siwfUrl = import.meta.env.VITE_SIWF_LOGIN_URL;
  if (siwfUrl && siwfUrl !== 'undefined') {
    window.location.href = siwfUrl;
  } else {
    router.push('/sign-in-with-x');
  }
};

const show = () => {
  isShowModal(true);
};
const toHome = () => {
  eventBus.emit("set-default-tab");
  return "/";
};

const toNotifications = () => {
  router.push("/notifications");
};

const toProfile = () => {
  if (userdata.value && userdata.value.id) {
    router.push(`/profile/${userdata.value.id}`);
  } else {
    console.error("User ID is missing");
  }
};
// Unified auth choice screen; optional intent param stored for preselecting action
const openAuthChoices = (intent) => {
  if (intent) {
    sessionStorage.setItem('authIntent', intent); // consumed by LoginMethod view if needed
  }
  router.push('/onboarding/login-method');
  localStorage.removeItem('activeTab');
};

// Direct path for Sign Up per updated requirement
const goCreateUsername = () => {
  sessionStorage.removeItem('authIntent');
  localStorage.removeItem('activeTab');
  router.push('/onboarding/create-username');
};

const logoClick = () => {
  router.push("/");
};

const currentScheme = useSchemeObserver();

const getAlertIcon = computed(() => {
  const scheme = currentScheme.value;
  const suffix = scheme === "light" ? "-dark" : "";
  const hasMessages = hasUnreadNotification.value;
  const isActive = router.currentRoute.value.path.startsWith("/notifications");

  if (isActive) {
    return hasMessages
      ? `/icon/alert-unread-fill${suffix}.svg`
      : `/icon/Alertfill${suffix}.svg`;
  }
  return hasMessages
    ? `/icon/alert-unread${suffix}.svg`
    : `/icon/Alert${suffix}.svg`;
});
// --- start---
// 🧱 Components reused from Rightbar
import BaseField from '@/components/ui-kit/BaseField/index.vue'
import SuggestedPost from '../Post/SuggestedPost.vue'
import CompactPost from '@/components/Post/CompactPost.vue'
// import GroupItem from '@/components/ui/GroupItem.vue'
import { debounce } from "@/utils/debounce";
// const store = useStore()

// 🟢 1. Search input binding
const searchText = computed({
  get: () => store.getters.getSearchText,
  set: value => store.dispatch('updateSearchText', value),
})

// 🟢 2. Fetching data from Vuex getters
const allUsers = computed(() => store.getters.getSearchUsers)
const allPosts = computed(() => store.getters.getSearchPosts)
const allGroups = computed(() => store.getters.getSearchGroups || [])

// 🟢 3. Debounced search API call
const fetchSearchResults = debounce(async () => {
  if (!searchText.value || !searchText.value.trim()) {
    store.commit('clearSearchResults')
    resetLists()
    isLoading.value = false;
    return
  }
  // await store.dispatch('fetchSearchResults', searchText.value)
  isLoading.value = true;
  try {
    await store.dispatch("fetchSearchResults", searchText.value);
  } finally {
    isLoading.value = false;
  }
}, 600)

// 🟢 4. Local pagination state
const pageSize = 5
const displayedUsers = ref([])
const displayedPosts = ref([])
const limitedGroups = computed(() => allGroups.value.slice(0, 3))

// 🟢 5. Watchers to update result lists
watch(searchText, (val) => {
  if (!val || !val.trim()) {
    store.commit('clearSearchResults')
    resetLists()
  }
})

watch(allUsers, (val) => {
  if (searchText.value && searchText.value.trim()) {
    displayedUsers.value = val.slice(0, pageSize)
  }
})

watch(allPosts, (val) => {
  if (searchText.value && searchText.value.trim()) {
    displayedPosts.value = val.slice(0, pageSize)
  }
})

// 🟢 6. Active status
const isSearchActive = computed(() =>
  !!searchText.value &&
  (allUsers.value.length || allPosts.value.length || allGroups.value.length)

)


// 🟢 7. Helpers
function resetLists() {
  displayedUsers.value = []
  displayedPosts.value = []
}

// 🟢 8. Follow group handler (same as rightbar)
async function followGroup(group) {
  try {
    const response = await store.dispatch('followGroup', group.id)
    if (response) group.following = true
  } catch (error) {
    console.error('Error following group:', error)
  }
}
function closeDropdown() {
  // Clear search text to close dropdown
  store.dispatch('updateSearchText', '')
  resetLists()
}
// const showAvatarDropdown = ref(false);

const user = ref({
  name: "Rajat Thakkar",
});

const groups = ref([
  { id: 1, name: "Vue Developers" },
  { id: 2, name: "Tech Enthusiasts" },
  { id: 3, name: "AI Innovators" },
]);

// function handleAvatarOption(option) {
//   console.log("Selected:", option);
// }

function handleGroupClick(group) {
  console.log("Clicked group:", group);
  // You can route like:
  // router.push(`/group/${group.id}`)
}
// ---end---
</script>

<style lang="scss" scoped>
.header {
  // position: fixed;
  padding: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  // height: var(--header-height);
  height: 56px;
  max-height: var(--header-height);
  // max-width: 1280px;
  // left: 50%;
  // transform: translateX(-50%); // center fixed header while allowing full width usage
  // padding: 16px 24px 16px 12px; // add right padding to align with search bar edge
  // padding: 9px 24px 7px 12px;
  z-index: 1200;
  // background: transparent; /* Use transparent to show app background gradient like sidebars */
  // background: #FFFFFF;
  // border-bottom: 1px solid #E5E7EB;
  background: var(--header-bg-light);
  border-bottom: 1px solid var(--header-border-light);
  color: var(--foreground-primary);
  // padding-left: 6rem;
  // padding-right: 6rem;

  &__inner {
    width: 100%;
    max-width: 1350px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
  }

  /* Wrapper to align any centered controls (e.g., search) exactly with centerbar column */
  &__middle {
    // flex: 1;
    width: 55%;
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
  }

  &__middle::before {
    content: '';
    position: absolute;
    left: 50%;
    top: 0;
    bottom: 0;
    width: var(--centerbar-width);
    /* use layout variable */
    max-width: var(--centerbar-width);
    margin-left: calc(var(--centerbar-width) / -2);
    pointer-events: none;
  }

  /* Any direct child placed inside header__middle that needs to align to feed width */
  &__middle>.aligned-center {
    width: 100%;
    max-width: var(--centerbar-width);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
  }

  .header-search {
    flex: 1;
  }

  &__rating {
    font-family: $clash;
    color: var(--foreground-primary);
    position: relative;
    cursor: help;
  }

  &__settings {
    display: flex;
    align-items: center;
    position: relative;
    // avatar wrapper only now; badge moved out of this block after reordering

    &:hover .header-arrow {
      transform: translateX(2px);
    }
  }

  @include _1400 {
    padding: 16px 24px 16px 12px;

    &__inner {
      grid-template-columns: 177px 1fr auto 60px;
      gap: 16px;
    }
  }

  @include _1279 {
    height: 64px;
    padding: 16px 48px 16px 20px; // match body padding (64px top already handled in body)

    &__inner {
      grid-template-columns: 120px 1fr auto 50px;
      gap: 12px;
    }
  }

  @include _1024 {
    padding: 16px;

    &__inner {
      grid-template-columns: 116px 1fr auto 50px;
      gap: 12px;
    }
  }

  @include _767 {
    padding: 16px 20px;

    &__inner {
      grid-template-columns: 120px 1fr auto 45px;
      gap: 8px;
    }
  }

  @include _575 {
    padding: 12px 16px;

    &__inner {
      grid-template-columns: 1fr auto 45px;
      gap: 8px;
    }
  }

  &__right {
    display: flex;
    align-items: center;
    justify-content: end;
    grid-template-columns: auto auto auto auto auto; // dropdown, avatar, badge, notif, theme - all auto-sized
    // column-gap: 8px;
    column-gap: 20px; // reduced from 12px to accommodate larger badge
    overflow: visible;
    padding-left: 8px; // prevent overflow

    >* {
      min-width: 0;
    }
  }

  /* (Breakpoint specific adjustments removed during debug shift; no overrides needed now) */

  &__logo {
    justify-content: flex-start;
  }

  &__notif {
    margin-left: 4px;
  }

  &__theme {
    margin-left: 4px;
  }

  // Badge wrapper and override styles for grid fit within space between avatar and notifications
  .badge-wrap {
    display: flex;
    align-items: center;
    white-space: nowrap;
    flex-shrink: 0;
    overflow: hidden;
  }

  :deep(.prominent-badge) {
    transform: none !important; // remove prior transform scaling
    font-size: 1.43rem; // increased by 50% from 0.95rem (0.95 * 1.5 = 1.425)
    padding: 3px 6px; // increased padding proportionally
    display: inline-flex;
    max-width: 100%;
    box-shadow: none; // optional: keep it calmer in header
    white-space: nowrap;
  }

  :deep(.prominent-badge .reputation-badge__symbol) {
    font-size: 0.9em;
  }

  :deep(.prominent-badge .reputation-badge__name) {
    font-size: 0.55em;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  // Remove positional shifts now that grid allocates space properly
  .current-org-drops,
  .header__settings {
    transform: none;
    margin-right: 0;
  }
}

// .current-org-drops {
//   @include _575 {
//     height: 6vh;
//   }
// }

.header-arrow {
  transition: 0.2s ease-in-out;
  width: 7px;
  margin-left: 4px;
}

@keyframes bellShake {

  0%,
  100% {
    transform: rotate(0deg);
  }

  10% {
    transform: rotate(-10deg);
  }

  20% {
    transform: rotate(10deg);
  }

  30% {
    transform: rotate(-10deg);
  }

  40% {
    transform: rotate(10deg);
  }

  50% {
    transform: rotate(-5deg);
  }

  60% {
    transform: rotate(5deg);
  }

  70% {
    transform: rotate(-5deg);
  }

  80% {
    transform: rotate(5deg);
  }

  90% {
    transform: rotate(0deg);
  }
}

.bell-shake {
  animation: bellShake 0.5s ease-in-out;
}

/* Guest buttons specific styles */
.guest-buttons {
  display: flex;
  align-items: center;
  justify-content: center;
  /* Center the buttons in their grid column */
  gap: 10px;
  width: 100%;
  min-width: 0;

  .about-link {
    display: flex;
    align-items: center;
    color: white; // Changed from var(--foreground-primary) to white
    font-size: 14px;
    white-space: nowrap;
    flex-shrink: 0;

    .about-text {
      margin-left: 4px;
    }
  }

  .sign-in-btn,
  .sign-up-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    padding: 6px 10px;
    flex-shrink: 0;
    min-width: 0;

    .button-icon {
      width: 16px;
      height: 16px;
      flex-shrink: 0;
      filter: brightness(0) saturate(100%) invert(0%);
      /* Make icons black */
    }

    .button-text {
      margin-left: 6px;
      white-space: nowrap;
    }
  }

  .frequency-btn {
    flex-shrink: 0;

    /* SIWFButton specific styling */
    button {
      height: 36px !important;
      min-width: 120px !important;
      font-size: 14px !important;
      padding: 6px 10px !important;
      border-radius: 6px !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
    }
  }
}

/* Theme container - right justified */
.theme-container {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;

  .theme-btn {
    flex-shrink: 0;
  }
}

/* Responsive styles for guest buttons */
@include _1024 {
  .guest-buttons {
    gap: 8px;

    .about-link {
      font-size: 13px;
      color: white; // Ensure white text on medium screens

      .about-text {
        margin-left: 3px;
      }
    }

    .sign-in-btn,
    .sign-up-btn {
      font-size: 13px;
      padding: 5px 8px;

      .button-text {
        margin-left: 4px;
      }
    }

    .frequency-btn {
      button {
        height: 34px !important;
        min-width: 110px !important;
        font-size: 13px !important;
        padding: 5px 8px !important;
      }
    }
  }
}

@include _767 {
  .guest-buttons {
    gap: 6px;

    .about-link {
      font-size: 12px;
      color: white; // Ensure white text on small screens

      .about-text {
        display: none;
        /* Hide text, keep icon */
      }
    }

    .sign-in-btn,
    .sign-up-btn {
      font-size: 12px;
      padding: 4px 6px;

      .button-text {
        display: none;
        /* Hide text, keep icon */
      }
    }

    .frequency-btn {
      button {
        height: 32px !important;
        width: 32px !important;
        /* Make it square for icon-only */
        min-width: 32px !important;
        padding: 4px !important;
        /* Minimal padding */
        justify-content: center !important;
      }

      /* Hide text on small screens */
      span {
        display: none !important;
      }

      /* Center the logo perfectly */
      div:first-child {
        margin-right: 0 !important;
      }
    }
  }
}

@include _575 {
  .guest-buttons {
    gap: 4px;

    .about-link {
      display: none;
      /* Hide completely on very small screens */
    }

    .sign-in-btn,
    .sign-up-btn {
      font-size: 11px;
      padding: 3px 5px;

      .button-text {
        display: none;
        /* Keep only icons */
      }

      .button-icon {
        width: 14px;
        height: 14px;
        filter: brightness(0) saturate(100%) invert(0%);
        /* Make icons black */
      }
    }

    .frequency-btn {
      button {
        height: 30px !important;
        width: 30px !important;
        /* Make it square for icon-only */
        min-width: 30px !important;
        padding: 3px !important;
        /* Minimal padding */
        justify-content: center !important;
      }

      /* Keep text hidden on very small screens */
      span {
        display: none !important;
      }

      /* Center the logo perfectly */
      div:first-child {
        margin-right: 0 !important;
      }
    }
  }
}

/* Username dropdown styling - use same background as Suggested for You section */
.current-org-drops {
  :deep(.p-dropdown) {
    background: var(--app-card-bg) !important;
    border: 1px solid var(--surface-border) !important;
    color: var(--foreground-primary) !important;
  }

  :deep(.p-dropdown-label) {
    color: var(--foreground-primary) !important;
  }

  :deep(.dropdown-menu__caption) {
    color: var(--foreground-primary) !important;
  }

  :deep(.p-dropdown-panel) {
    background: var(--app-card-bg) !important;
    border: 1px solid var(--surface-border) !important;
    color: var(--foreground-primary) !important;
  }

  :deep(.p-dropdown-item) {
    color: var(--foreground-primary) !important;
    background: transparent !important;
  }

  :deep(.p-dropdown-item:hover) {
    background: var(--app-card-bg-hover) !important;
    color: var(--foreground-primary) !important;
  }

  /* Ensure all text elements are visible */
  :deep(span),
  :deep(div) {
    color: var(--foreground-primary) !important;
  }
}

/* More specific PrimeVue dropdown styling for username dropdown */
.current-org-drops.p-dropdown {
  background: var(--app-card-bg) !important;
  border: 1px solid var(--surface-border) !important;
  color: var(--foreground-primary) !important;
}

.current-org-drops .p-dropdown {
  background: var(--app-card-bg) !important;
  border: 1px solid var(--surface-border) !important;
  color: var(--foreground-primary) !important;
}

.current-org-drops .p-dropdown-panel {
  background: var(--app-card-bg) !important;
  border: 1px solid var(--surface-border) !important;
  color: var(--foreground-primary) !important;
}

.current-org-drops .p-dropdown-item {
  color: var(--foreground-primary) !important;
  background: transparent !important;
}

.current-org-drops .p-dropdown-item:hover {
  background: var(--app-card-bg-hover) !important;
  color: var(--foreground-primary) !important;
}

/* Text visibility overrides */
.current-org-drops .p-dropdown-label,
.current-org-drops .dropdown-menu__caption {
  color: var(--foreground-primary) !important;
}

/* Header Dropdown specific avatar fix */
.header-dropdown-avatar .avatar {
  width: 25px !important;
  height: 25px !important;
  border-radius: 50% !important;
}

.header-dropdown-avatar .avatar-img {
  width: 100% !important;
  height: 100% !important;
  object-fit: cover !important;
  border-radius: 50% !important;
}

/* PrimeVue ke distortion ko fix karo */
.current-org-drops .p-dropdown-item,
.current-org-drops .p-dropdown-label {
  line-height: normal !important;
  padding: 4px 8px !important;
  display: flex;
  align-items: center;
  gap: 6px;
}

/* ---------------------------------------
 ✅ BaseAvatar styling only inside header dropdown
----------------------------------------*/
.current-org-drops {

  /* VALUE (selected profile) */
  ::v-deep(.p-dropdown-label .header-dropdown-avatar.avatar) {
    width: 28px !important;
    height: 28px !important;
  }

  ::v-deep(.p-dropdown-label .avatar-ring) {
    width: 32px !important;
    height: 32px !important;
    padding: 2px !important;
  }

  ::v-deep(.p-dropdown-label .avatar-img) {
    width: 100% !important;
    height: 100% !important;
    object-fit: cover !important;
    border-radius: 50% !important;
  }


  /* OPTIONS (inside dropdown panel) */
  ::v-deep(.p-dropdown-panel .p-dropdown-item .header-dropdown-avatar.avatar) {
    width: 28px !important;
    height: 28px !important;
  }

  ::v-deep(.p-dropdown-panel .p-dropdown-item .avatar-ring) {
    width: 32px !important;
    height: 32px !important;
    padding: 2px !important;
  }

  ::v-deep(.p-dropdown-panel .p-dropdown-item .avatar-img) {
    width: 100% !important;
    height: 100% !important;
    object-fit: cover !important;
    border-radius: 50% !important;
  }
}



// .header-dropdown-avatar .avatar-img {
//   width: 100% !important;
//   height: 100% !important;
//   object-fit: cover !important;
//   border-radius: 50% !important;
// }

/* PrimeVue ke distortion ko fix karo */
.current-org-drops .p-dropdown-item,
.current-org-drops .p-dropdown-label {
  line-height: normal !important;
  padding: 4px 8px !important;
  display: flex;
  align-items: center;
  gap: 6px;
}
</style>
<style lang="scss">
/* Avatar inside dropdown options */
.header-dropdown-panel .header-dropdown-avatar .avatar {
  width: 28px !important;
  height: 28px !important;
  border-radius: 50% !important;
}

.header-dropdown-panel .header-dropdown-avatar .avatar-img {
  width: 100% !important;
  height: 100% !important;
  object-fit: cover !important;
  border-radius: 50% !important;
}

/* Dropdown option layout */
.header-dropdown-panel .p-dropdown-item {
  display: flex !important;
  align-items: center !important;
  gap: 8px !important;
}

.hamburger button {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  border-radius: 8px;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }

  svg {
    transition: transform 0.25s ease;
  }

  &:active svg {
    transform: scale(0.9);
  }
}

@include _575 {

  // 🔹 Show hamburger icon on mobile
  .hamburger {
    display: block !important;
  }

  // 🔹 Keep the right section visible, but hide only dropdown + theme switcher
  .header__right {
    display: flex !important;
    align-items: center;

    .current-org-drops {
      display: none !important; // Hide the profile/org dropdown
    }

    // .theme-btn {
    //   display: none !important; // Hide ColorSchemeSwitcher
    // }
  }
}

@media (min-width: 576px) {

  // 🔹 Hide hamburger on desktop
  .hamburger {
    display: none !important;
  }

  // 🔹 Show dropdown + theme switcher again
  .header__right {
    .current-org-drops {
      display: flex !important;
    }

    .theme-btn {
      display: flex !important;
    }
  }
}

.hamburger-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 35px;
  height: 44px;
  cursor: pointer;
  border-radius: 10px;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }

  svg {
    transition: transform 0.25s ease;
  }

  &:active svg {
    transform: scale(0.9);
  }
}

.bar {
  height: 3px;
  width: 100%;
  background: var(--text-primary, #333);
  border-radius: 3px;
  transition: all 0.3s ease;
}

/* Transform to X when open */
.hamburger-btn.open .bar:nth-child(1) {
  transform: rotate(45deg) translateY(8px);
}

.hamburger-btn.open .bar:nth-child(2) {
  opacity: 0;
}

.hamburger-btn.open .bar:nth-child(3) {
  transform: rotate(-45deg) translateY(-8px);
}

/* --- Mobile Dropdown Animation --- */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease;
}

.slide-down-enter-from,
.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* --- Mobile Dropdown Styling --- */
.mobile-menu {
  position: absolute;
  top: 70px;
  left: 0;
  width: 100%;
  background: var(--theme-wise-background);
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.1);
  border-radius: 0 0 16px 16px;
  // padding: 1.25rem 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: start;
  gap: 1rem;
  z-index: 999;
  animation: fadeIn 0.3s ease;
}

/* Subtle divider line between menu items */
.mobile-menu>*:not(:last-child) {
  width: 85%;
  border-bottom: 1px solid rgba(0, 0, 0, 0.07);
  padding-bottom: 0.75rem;
}

/* Slight extra space after divider */
.mobile-menu>*:not(:first-child) {
  margin-top: 0.75rem;
}

/* Smooth dropdown animation */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease;
}

.slide-down-enter-from,
.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-12px);
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Dropdown styling */
.mobile-org-dropdown {
  width: 85%;
  max-width: 320px;
  font-weight: 500;
}

.mobile-menu .p-dropdown {
  width: 100%;
  border-radius: 10px;
  background: var(--theme-wise-surface, #fff);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.mobile-menu .p-dropdown-label {
  padding: 0.75rem 1rem;
  font-size: 0.95rem;
}

/* Switcher styling */
.mobile-menu .ColorSchemeSwitcher {
  margin-bottom: 0;
}
</style>